import type { ChecklistItem } from '../types/schema'
import { buildLockGraph, resolveLock } from '../services/checklistLockService'
import { formatZonedDateKey, getWeekBucketKey, resolveWorkspaceTimeZone } from '../services/repeat/keys'
import type { RepeatPagesInput } from '../services/repeat/types'

export type ChecklistBlockReasonCode =
  | 'DEPENDENCY_INCOMPLETE'
  | 'DEPENDENCY_MISSING'
  | 'PROOF_REQUIRED'
  | 'LOCATION_REQUIRED'
  | 'RESOURCE_WARNING'
  | 'EXTERNAL_BLOCKER'

export type ChecklistBlockReason = {
  code: ChecklistBlockReasonCode
  targetId?: string
  title?: string
  inBin?: boolean
}

export const isChecklistGate = (item: Pick<ChecklistItem, 'nodeKind'> | null | undefined): boolean =>
  item?.nodeKind === 'GATE_AND' || item?.nodeKind === 'GATE_OR'

export const getChecklistBlockState = (
  item: ChecklistItem,
  itemById: ReadonlyMap<string, ChecklistItem>,
  periodKey?: string,
  pages?: RepeatPagesInput,
  timeZone?: string,
) => {
  const graph = buildLockGraph(Array.from(itemById.values()))
  const tz = resolveWorkspaceTimeZone(timeZone)
  const resolvedPeriodKey = periodKey ?? (item.checklistType === 'WEEKLY' ? getWeekBucketKey(new Date(), tz) : formatZonedDateKey(new Date(), tz))
  const lock = resolveLock(item, resolvedPeriodKey, graph, pages, undefined, tz)

  const reasons: ChecklistBlockReason[] = lock.reasons.map((r) => ({
    code: r.code as ChecklistBlockReasonCode,
    targetId: r.targetId,
    title: r.title,
    inBin: r.inBin,
  }))

  if (item.requiresProofFile === true && !item.proofFileUrl && !item.proofSubmissionId) {
    if (!reasons.some((r) => r.code === 'PROOF_REQUIRED')) reasons.push({ code: 'PROOF_REQUIRED' })
  }
  if (item.requiresLocationPin === true && !item.verifiedCoordinates) {
    if (!reasons.some((r) => r.code === 'LOCATION_REQUIRED')) reasons.push({ code: 'LOCATION_REQUIRED' })
  }
  if ((item as ChecklistItem & { resourceGateStatus?: string }).resourceGateStatus === 'WARNING') {
    if (!reasons.some((r) => r.code === 'RESOURCE_WARNING')) reasons.push({ code: 'RESOURCE_WARNING' })
  }
  if (item.blockedByExternal) {
    if (!reasons.some((r) => r.code === 'EXTERNAL_BLOCKER')) reasons.push({ code: 'EXTERNAL_BLOCKER', targetId: item.blockedByExternal.targetId })
  }

  return {
    isBlocked: lock.isBlocked || reasons.length > 0,
    reasons,
    blockerCount: reasons.filter((reason) => reason.code === 'DEPENDENCY_INCOMPLETE' || reason.code === 'DEPENDENCY_MISSING').length,
  }
}
